import { getBreadcrumbs } from 'server/utils/breadcrumbs/getBreadcrumbs';
import { fetchGlobalPricingId } from 'server/utils/fetchGlobalPricingId';
import { IProductLandingPage } from 'shared/types/ProductLandingPage';
import { PlpPageComponent as PLPLandingPageComponent } from 'product-landing-pages/components/PlpPageComponent';
import { startCase } from 'shared/utils/startCase';
import { getBrowseResults } from 'server/utils/constructor/getBrowseResults';
import { isSearchCrawler } from 'shared/utils/isSearchCrawler';
import { setServerSideTranslations } from 'localization/utils/setServerSideTranslations';

interface ICategorizedColorPage extends IProductLandingPage {
  metaDescription?: string;
}

const CategorizedColorPage = ({
  breadcrumbs,
  browseKey,
  browseValue,
  category,
  colorValue,
  constructorResponse,
  displayName,
  globalPricingId,
  isCrawler = false,
}: ICategorizedColorPage) => {
  const {
    name = displayName,
    description = '',
    metadataDescription = '',
    titleTag = '',
  } = breadcrumbs || {};

  return (
    <PLPLandingPageComponent
      breadcrumbs={[
        { name: startCase(category), slug: `/${category}` },
        {
          name: startCase(colorValue),
          slug: `/`,
          isStatic: true,
        },
      ]}
      browseKey={browseKey}
      browseValue={browseValue}
      constructorResponse={constructorResponse}
      description={description}
      displayName={name}
      globalPricingId={globalPricingId}
      metadataDescription={metadataDescription}
      metaTitle={titleTag}
      isCrawler={isCrawler}
    />
  );
};

export const getServerSideProps = async ({ query, resolvedUrl, req, res, locale }) => {
  const { pageSlug: category, colorValue }: { pageSlug: string; colorValue: string } = query;
  const breadcrumbs = await getBreadcrumbs({
    req,
    res,
    path: `${category}/color/${colorValue}`,
  });

  const isCrawler = isSearchCrawler(req?.headers);

  try {
    const globalPricingId = await fetchGlobalPricingId({ req, res });
    // capitalizes the first letter (accounts for multi-color which should be Multi-color)
    const colorPresentationName: string = colorValue.charAt(0).toUpperCase() + colorValue.slice(1);
    const categoryName: string = startCase(category);
    const displayName = `${colorPresentationName} ${categoryName}`;
    const colorBrowseName: string = colorValue.toLowerCase();

    const colorResponse = await getBrowseResults({
      req,
      res,
      query,
      resolvedUrl,
      filterKey: 'color',
      filterValue: colorBrowseName,
    });

    const invalidSearch = colorResponse.response.total_num_results === 0;

    if (invalidSearch) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        ...(await setServerSideTranslations({ locale })),
        breadcrumbs,
        browseKey: 'color',
        browseValue: colorBrowseName,
        category,
        colorValue,
        constructorResponse: colorResponse,
        displayName,
        globalPricingId,
        isCrawler,
      },
    };
  } catch {
    return {
      notFound: true,
    };
  }
};

export default CategorizedColorPage;
